import { Injectable, signal } from '@angular/core';
import { GAME_SCREEN_LAYOUT } from '@libs/store/game/model';
import { APP } from '@libs/interfaces';

@Injectable({
  providedIn: 'root',
})
export class LocalstorageService {
  app_selected = signal<APP>(this.app);
  bet_reverse_mode = signal<boolean>(false);
  constructor() {
    this.betReverseMode();
  }
  /**
   * Get the bet reverse mode from local storage.
   * @returns {boolean} The bet reverse mode state.
   */
  private betReverseMode(): boolean {
    const value = this.getItem(this.betReverseModeKey);
    this.bet_reverse_mode.set(value === 'true');
    return this.bet_reverse_mode();
  }

  /**
   * Store the bet reverse mode in local storage.
   * @param {boolean} mode - The bet reverse mode state.
   */
  storeBetReverseMode(mode: boolean): void {
    this.setItem(this.betReverseModeKey, String(mode));
    this.bet_reverse_mode.set(mode);
  }

  /**
   * Get the key for the bet reverse mode.
   * @returns {string} The key for the bet reverse mode.
   */
  private get betReverseModeKey(): string {
    return 'APP_BET_REVERSE_MODE';
  }
  /**
   * Store the user's email in local storage.
   * @param {string} email - The user's email.
   */
  storeEmail(email: string): void {
    this.setItem(this.emailKey, email);
  }

  /**
   * Get the email from local storage.
   * @returns {string} The email.
   */
  get email(): string {
    return this.getItem(this.emailKey);
  }

  /**
   * Get the key for the email.
   * @returns {string} The key for the email.
   */
  private get emailKey(): string {
    return 'APP_EMAIL';
  }

  /**
   * Store the phone number in local storage.
   * @param {number} phone - The phone number.
   */
  storePhone(phone: string): void {
    this.setItem(this.phoneKey, phone);
  }

  /**
   * Get the phone number from local storage.
   * @returns {number | null} The phone number.
   */
  get phone(): number | null {
    const phone = this.getItem(this.phoneKey);
    return phone ? +phone : null;
  }

  /**
   * Get the key for the phone number.
   * @returns {string} The key for the phone number.
   */
  private get phoneKey(): string {
    return 'APP_PHONE_KEY';
  }

  /**
   * Set the referral data in local storage.
   * @param {any} data - The referral data.
   */
  set referral(data: any) {
    this.setItem(this.referralKey, JSON.stringify(data));
  }

  /**
   * Get the referral data from local storage.
   * @returns {any} The referral data.
   */
  get referral(): any {
    const referral = this.getItem(this.referralKey);
    const res = referral ? JSON.parse(referral) : null;
    return res?.exp < Date.now() ? null : res;
  }

  /**
   * Get the key for the referral data.
   * @returns {string} The key for the referral data.
   */
  private get referralKey(): string {
    return 'REFERRAL_KEY';
  }

  /**
   * Get the app type from local storage.
   * @returns {APP} The app type.
   */
  get app(): APP {
    const app = this.getItem(this.appTypeKey) as APP;
    return app;
  }

  /**
   * Set the app type in local storage.
   * @param {APP} type - The app type.
   */
  set app(type: APP) {
    this.setItem(this.appTypeKey, type);
    this.app_selected.set(type);
  }

  /**
   * Get the key for the app type.
   * @returns {string} The key for the app type.
   */
  private get appTypeKey(): string {
    return 'APP_TYPE';
  }

  /**
   * Store the refresh token in local storage.
   * @param {string} refreshToken - The refresh token.
   */
  storeRefreshToken(refreshToken: string): void {
    this.setItem(this.refreshTokenKey, refreshToken);
  }

  /**
   * Get the refresh token from local storage.
   * @returns {string} The refresh token.
   */
  get refreshToken(): string {
    return this.getItem(this.refreshTokenKey);
  }

  /**
   * Remove the authentication tokens from local storage.
   */
  removeTokens(): void {
    this.removeItem(this.refreshTokenKey);
  }

  /**
   * Get the key for the refresh token.
   * @returns {string} The key for the refresh token.
   */
  private get refreshTokenKey(): string {
    return 'APP_REFRESH_JWT_KEY';
  }

  /**
   * Store the game screen layout in local storage.
   * @param {GAME_SCREEN_LAYOUT} layout - The game screen layout.
   * @returns {GAME_SCREEN_LAYOUT} The stored game screen layout.
   */
  storeGameScreenLayout(layout: GAME_SCREEN_LAYOUT): GAME_SCREEN_LAYOUT {
    localStorage.setItem('GAME_SCREEN_LAYOUT', layout);
    return layout;
  }

  /**
   * Get the key for the sports path.
   * @returns {string} The key for the sports path.
   */
  private get sportsPathKey(): string {
    return 'BT-PATH';
  }

  /**
   * Get the sports path from local storage.
   * @returns {string} The sports path.
   */
  get sportsPath(): string {
    return this.getItem(this.sportsPathKey);
  }

  /**
   * Set the sports path in local storage.
   * @param {string} path - The sports path.
   */
  set sportsPath(path: string) {
    this.setItem(this.sportsPathKey, path);
  }
  /*
  /**
   * Store the bonus value in local storage.
   * @param {string} bonus - The bonus value.
   */
  storeBonusFirstDeposit(bonus: { code: string; expireAt: number }): void {
    this.setItem(this.bonusFirstDepositKey, JSON.stringify(bonus));
  }

  /**
   * Get the bonus value from local storage.
   * @returns {string} The bonus value.
   */
  get bonusFirstDeposit(): { code: string; expireAt: number } {
    const bonus = this.getItem(this.bonusFirstDepositKey);
    const res = bonus ? JSON.parse(bonus) : null;
    return res?.expireAt < Date.now() ? null : res;
  }
  /**
   * Removes an item from localStorage if it has expired based on an expireAt timestamp
   * @param {string} key - The key to check
   * @param {string} expireAtField - The field name containing the expiration timestamp
   * @returns {boolean} True if item was expired and removed, false otherwise
   */
  removeIfExpiredBonusFirstDeposit() {
    const res = this.bonusFirstDeposit;
    if (res && res.expireAt < Date.now()) {
      this.removeItem(this.bonusFirstDepositKey);
    }
  }
  /**
   * Get the key for the bonus value.
   * @returns {string} The key for the bonus value.
   */
  private get bonusFirstDepositKey(): string {
    return 'APP_BONUS_FIRST_DEPOSIT_KEY';
  }

  /**
   * Helper method to get an item from local storage.
   * @param {string} key - The key of the item.
   * @returns {string} The item from local storage.
   */
  private getItem(key: string): string {
    return window.localStorage.getItem(key) || '';
  }

  /**
   * Helper method to set an item in local storage.
   * @param {string} key - The key of the item.
   * @param {string} value - The value of the item.
   */
  private setItem(key: string, value: string): void {
    window.localStorage.setItem(key, value);
  }

  /**
   * Helper method to remove an item from local storage.
   * @param {string} key - The key of the item.
   */
  private removeItem(key: string): void {
    window.localStorage.removeItem(key);
  }
}
